import { defineMessage } from 'react-intl';
// ---------------------------------------------------------------------------
// Labels — defined here as the single source of truth so individual hooks
// no longer need to define them.
// ---------------------------------------------------------------------------
const NEW_THREAD_LABEL = defineMessage({
    defaultMessage: 'New Session',
    description: 'Label for the keyboard shortcut that creates a new conversation thread',
    id: 'eb3b1320bf',
});
const SHOW_SHORTCUTS_LABEL = defineMessage({
    defaultMessage: 'Show Shortcuts',
    description: 'Label for the keyboard shortcut that displays the keyboard shortcuts panel',
    id: '1a5ee274b1',
});
const FOCUS_ASK_INPUT_LABEL = defineMessage({
    defaultMessage: 'Focus Ask Input',
    description: 'Label for the keyboard shortcut that focuses the search/ask input field',
    id: '83ab14a407',
});
const FOCUS_FINANCE_SEARCH_LABEL = defineMessage({
    defaultMessage: 'Focus Finance Search',
    description: 'Label for the keyboard shortcut that focuses the finance ticker search input',
    id: 'c7f03519bd',
});
const DICTATION_LABEL = defineMessage({
    defaultMessage: 'Toggle Dictation',
    description: 'Label for the keyboard shortcut that toggles voice dictation (Windows app only)',
    id: '4c8b313418',
});
const STOP_GENERATING_LABEL = defineMessage({
    defaultMessage: 'Stop Generating',
    description: 'Label for the keyboard shortcut that stops the current AI response',
    id: '35c55384bc',
});
const ALL_SETTINGS_LABEL = defineMessage({
    defaultMessage: 'All Settings',
    description: 'Label for the keyboard shortcut that opens the main settings page',
    id: 'e8cc884924',
});
const OPEN_COMMAND_PALETTE_LABEL = defineMessage({
    defaultMessage: 'Search sessions',
    description: 'Label for the keyboard shortcut that opens session search',
    id: '3fdf17cba8',
});
const SEARCH_MODE_LABEL = defineMessage({
    defaultMessage: 'Mode: Search',
    description: 'Label for the keyboard shortcut that switches to search mode',
    id: 'd7d3d00cd0',
});
const COMPUTER_MODE_LABEL = defineMessage({
    defaultMessage: 'Mode: Computer',
    description: 'Label for the keyboard shortcut that switches to computer mode',
    id: 'e40b285783',
});
const TOGGLE_INCOGNITO_LABEL = defineMessage({
    defaultMessage: 'Toggle Incognito',
    description: 'Label for the keyboard shortcut that toggles incognito/private mode',
    id: '912a8a1890',
});
const TOGGLE_SIDEBAR_LABEL = defineMessage({
    defaultMessage: 'Toggle Sidebar',
    description: 'Label for the keyboard shortcut that toggles the sidebar open or closed',
    id: '87cb377521',
});
const TOGGLE_ASSET_CHROME_LABEL = defineMessage({
    defaultMessage: 'Toggle Artifact UI',
    description: 'Label for the keyboard shortcut that toggles the Perplexity UI around a Computer artifact viewer',
    id: '3ff4e8f196',
});
const CLOSE_SIDE_PANEL_LABEL = defineMessage({
    defaultMessage: 'Close Side Panel',
    description: 'Label for the keyboard shortcut that closes the focused side panel',
    id: '350421056d',
});
const TOGGLE_SIDE_CHAT_LABEL = defineMessage({
    defaultMessage: 'Toggle Side Chat',
    description: 'Label for the keyboard shortcut that opens or closes a Computer side chat',
    id: '0a17014840',
});
const SHARE_LABEL = defineMessage({
    defaultMessage: 'Share',
    description: 'Label for the keyboard shortcut that toggles the share dropdown',
    id: 'c1cc285f0d',
});
const FILE_UPLOAD_LABEL = defineMessage({
    defaultMessage: 'Upload File',
    description: 'Label for the keyboard shortcut that opens the file upload dialog',
    id: '2debe8d795',
});
const APPROVE_WORKFLOW_LABEL = defineMessage({
    defaultMessage: 'Approve',
    description: 'Label for the keyboard shortcut that approves a pending workflow action',
    id: 'aaf41ce142',
});
const SUBMIT_WORKFLOW_QUESTIONS_LABEL = defineMessage({
    defaultMessage: 'Submit',
    description: 'Label for the keyboard shortcut that submits workflow questions',
    id: 'ea9a6deb28',
});
const EXECUTE_INLINE_EDIT_BATCH_LABEL = defineMessage({
    defaultMessage: 'Execute inline edits',
    description: 'Label for the keyboard shortcut that executes the queued inline edit batch',
    id: 'ab2c0a9e08',
});
const DENY_WORKFLOW_LABEL = defineMessage({
    defaultMessage: 'Deny',
    description: 'Label for the keyboard shortcut that denies a pending workflow action',
    id: '393b7117f1',
});
// ---------------------------------------------------------------------------
// Manifest
//
// Typed as `Record<ShortcutId, ShortcutManifestEntry>` so the compiler
// enforces that every member of `ShortcutId` has a corresponding entry.
// Adding a new ID to the union without a manifest entry (or vice-versa)
// produces a type error.
// ---------------------------------------------------------------------------
/**
 * Static catalog of every supported shortcut and its default metadata.
 *
 * Add new shortcuts by updating both the `ShortcutId` union and this manifest.
 * The `Record<ShortcutId, ShortcutManifestEntry>` type makes omissions a compile
 * error while preserving explicit `ShortcutId` declarations for isolated builds.
 */
export const SHORTCUT_MANIFEST = {
    // -- General --
    'new-thread': {
        defaultKeybinding: { mac: 'cmd+k', default: 'ctrl+i' },
        label: NEW_THREAD_LABEL,
        group: 'general',
        mode: 'Search',
    },
    'focus-ask-input': {
        defaultKeybinding: { mac: ['cmd+j', '/'], default: ['ctrl+j', '/'] },
        label: FOCUS_ASK_INPUT_LABEL,
        group: 'general',
    },
    'focus-finance-search': {
        defaultKeybinding: { mac: '/', default: '/' },
        label: FOCUS_FINANCE_SEARCH_LABEL,
        group: 'navigation',
        mode: 'Finance',
    },
    'stop-generating': {
        defaultKeybinding: { mac: 'escape', default: 'escape' },
        label: STOP_GENERATING_LABEL,
        group: 'general',
    },
    'toggle-dictation': {
        defaultKeybinding: { mac: 'ctrl+d', default: 'ctrl+d' },
        label: DICTATION_LABEL,
        group: 'general',
    },
    share: {
        defaultKeybinding: { mac: 'shift+cmd+c', default: 'shift+ctrl+c' },
        label: SHARE_LABEL,
        group: 'general',
    },
    'file-upload': {
        defaultKeybinding: { mac: 'cmd+u', default: 'ctrl+u' },
        label: FILE_UPLOAD_LABEL,
        group: 'general',
    },
    'approve-workflow': {
        defaultKeybinding: { mac: 'cmd+enter', default: 'ctrl+enter' },
        label: APPROVE_WORKFLOW_LABEL,
        group: 'general',
    },
    'deny-workflow': {
        defaultKeybinding: { mac: 'cmd+escape', default: 'ctrl+escape' },
        label: DENY_WORKFLOW_LABEL,
        group: 'general',
    },
    'submit-workflow-questions': {
        defaultKeybinding: { mac: 'cmd+enter', default: 'ctrl+enter' },
        label: SUBMIT_WORKFLOW_QUESTIONS_LABEL,
        group: 'general',
    },
    'execute-inline-edit-batch': {
        defaultKeybinding: { mac: 'cmd+enter', default: 'ctrl+enter' },
        label: EXECUTE_INLINE_EDIT_BATCH_LABEL,
        group: 'general',
    },
    'all-settings': {
        defaultKeybinding: { mac: 'shift+cmd+,', default: 'shift+ctrl+,' },
        label: ALL_SETTINGS_LABEL,
        group: 'navigation',
    },
    'open-command-palette': {
        defaultKeybinding: { mac: 'cmd+k', default: 'ctrl+k' },
        label: OPEN_COMMAND_PALETTE_LABEL,
        group: 'navigation',
    },
    'home-search-mode': {
        defaultKeybinding: { mac: 'shift+cmd+1', default: 'shift+ctrl+1' },
        label: SEARCH_MODE_LABEL,
        group: 'navigation',
        mode: 'Home',
    },
    'home-computer-mode': {
        defaultKeybinding: { mac: 'shift+cmd+2', default: 'shift+ctrl+2' },
        label: COMPUTER_MODE_LABEL,
        group: 'navigation',
        mode: 'Home',
    },
    // -- Interface --
    'show-shortcuts': {
        defaultKeybinding: { mac: 'cmd+/', default: 'ctrl+/' },
        label: SHOW_SHORTCUTS_LABEL,
        group: 'interface',
    },
    'toggle-incognito': {
        defaultKeybinding: { mac: 'cmd+;', default: 'ctrl+;' },
        label: TOGGLE_INCOGNITO_LABEL,
        group: 'interface',
    },
    'toggle-sidebar': {
        defaultKeybinding: { mac: 'cmd+.', default: 'ctrl+.' },
        label: TOGGLE_SIDEBAR_LABEL,
        group: 'interface',
    },
    'toggle-asset-chrome': {
        defaultKeybinding: { mac: 'cmd+.', default: 'ctrl+.' },
        label: TOGGLE_ASSET_CHROME_LABEL,
        group: 'interface',
        mode: 'Computer',
    },
    'close-side-panel': {
        defaultKeybinding: { mac: 'escape', default: 'escape' },
        label: CLOSE_SIDE_PANEL_LABEL,
        group: 'interface',
    },
    'toggle-side-chat': {
        defaultKeybinding: { mac: 'cmd+alt+s', default: 'ctrl+alt+s' },
        label: TOGGLE_SIDE_CHAT_LABEL,
        group: 'interface',
        mode: 'Computer',
    },
};
/**
 * Returns the manifest entry for a shortcut ID as a full `ShortcutDefinition`
 * (including the `id` field).
 *
 * Because `SHORTCUT_MANIFEST` is typed as `Record<ShortcutId, …>`, every
 * member of the `ShortcutId` union is guaranteed to have a corresponding
 * entry — no runtime assertion or fallback is needed.
 */
export function getShortcutDefinition(id) {
    return { id, ...SHORTCUT_MANIFEST[id] };
}
/**
 * Resolves the keybinding for a given platform from a `PlatformKeybinding`.
 *
 * Always returns an array — single-string entries in the manifest are
 * normalized to a one-element array so consumers never have to branch on
 * the union type.
 */
export function resolvePlatformKeybinding(platformKeybinding, isMacOS) {
    const raw = isMacOS ? platformKeybinding.mac : platformKeybinding.default;
    return Array.isArray(raw) ? raw : [raw];
}
/**
 * Returns whether a shortcut should be exposed in shortcut display surfaces.
 *
 * Mobile currently hides all keyboard shortcut hints. This mirrors
 * `useRegisterShortcut`, which also forces registrations to `isEnabled: false`
 * on mobile so hidden shortcuts do not intercept keyboard events.
 */
export function isShortcutVisibleOnCurrentDevice(_definition, isMobileDevice) {
    return !isMobileDevice;
}
//# sourceMappingURL=shortcutManifest.js.map